/** Sheets import API (owner only). Kalitlar ["import", ...]: real-time "import" hodisasi shularni yangilaydi. */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Paginated } from "@/lib/api";

export type ImportStatus = "uploaded" | "mapped" | "previewed" | "importing" | "done" | "failed";
export type OwnerStrategy = "column" | "single" | "pool";

export interface Option {
  key: string;
  label: string;
}

export interface ImportStats {
  rows: number;
  valid: number;
  duplicates: number;
  errors: number;
  reformatted_phones: number;
  error_reasons: Record<string, number>;
  status_unmapped: string[];
  owners_unresolved: number;
  imported?: number;
}

export interface ImportErrorRow {
  row: number;
  reason: string;
  raw: string[];
  duplicate?: boolean;
}

export interface ImportJob {
  id: number;
  source_type: "file" | "gsheet";
  file_name: string;
  sheet_url: string;
  sheet_tabs: string[];
  status: ImportStatus;
  columns: string[];
  sample_rows: string[][];
  row_count: number;
  mapping: Record<string, string>;
  status_values: string[];
  status_map: Record<string, string>;
  owner_strategy: OwnerStrategy;
  default_owner: number | null;
  default_branch: number | null;
  default_source: string;
  stats: Partial<ImportStats>;
  error_rows: ImportErrorRow[];
  imported_count: number;
  error_message: string;
  finished_at: string | null;
  created_at: string;
  created_by_name: string | null;
  targets: Option[];
  statuses: Option[];
  sources: Option[];
  service_account_email: string;
  version: number;
}

export interface ImportMappingPatch {
  mapping?: Record<string, string>;
  status_map?: Record<string, string>;
  owner_strategy?: OwnerStrategy;
  default_owner?: number | null;
  default_branch?: number | null;
  default_source?: string;
}

export const importKeys = {
  all: ["import"] as const,
  list: ["import", "list"] as const,
  detail: (id: number) => ["import", "detail", id] as const,
};

export function useImports() {
  return useQuery({
    queryKey: importKeys.list,
    queryFn: () => api.get<Paginated<ImportJob>>("/api/imports/", { query: { page_size: 20 } }),
    placeholderData: keepPreviousData,
  });
}

export function useImportJob(id: number | null) {
  return useQuery({
    queryKey: importKeys.detail(id ?? 0),
    queryFn: () => api.get<ImportJob>(`/api/imports/${id}/`),
    enabled: id !== null,
  });
}

/** 1-qadam: fayl yoki Google Sheets havolasi */
export function useCreateImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { file?: File; sheet_url?: string; sheet_tab?: string }) => {
      if (input.file) {
        const fd = new FormData();
        fd.append("file", input.file);
        return api.post<ImportJob>("/api/imports/", undefined, { formData: fd });
      }
      return api.post<ImportJob>("/api/imports/", { sheet_url: input.sheet_url, sheet_tab: input.sheet_tab || undefined });
    },
    onSuccess: (job) => {
      qc.setQueryData(importKeys.detail(job.id), job);
      qc.invalidateQueries({ queryKey: importKeys.list });
    },
  });
}

/** 2-qadam: ustunlar va sozlamalar */
export function useUpdateImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: ImportMappingPatch & { id: number }) => api.patch<ImportJob>(`/api/imports/${id}/`, data),
    onSuccess: (job) => qc.setQueryData(importKeys.detail(job.id), job),
  });
}

/** 3-qadam: tekshirish (hech narsa yozilmaydi) */
export function usePreviewImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<ImportJob>(`/api/imports/${id}/preview/`),
    onSuccess: (job) => qc.setQueryData(importKeys.detail(job.id), job),
  });
}

/** 4-qadam: import (bitta tranzaksiya) */
export function useRunImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<ImportJob & { imported: number }>(`/api/imports/${id}/run/`),
    onSuccess: (job) => {
      qc.setQueryData(importKeys.detail(job.id), job);
      qc.invalidateQueries({ queryKey: importKeys.list });
      qc.invalidateQueries({ queryKey: ["driver"] });
    },
  });
}

// DRF action manzillari oxirida "/" bilan (slash'siz 301 qaytadi)
export const errorsCsvUrl = (id: number) => `/api/imports/${id}/errors.csv/`;
export const templateCsvUrl = "/api/imports/template.csv/";
